import { extend } from "@dhx/ts-common/core";
import { IHandlers } from "@dhx/ts-common/types";
import { create, el, VNode } from "@dhx/ts-common/dom";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { calculatePosition, getRealPosition, locate, locateNode } from "@dhx/ts-common/html";
import { keyManager } from "@dhx/ts-common/Keymanager";
import { View } from "@dhx/ts-common/view";

import { TreeCollection, IDataEventsHandlersMap } from "@dhx/ts-data";
import { DataEvents, IItem, IGroups, ItemType, NavigationType, NavigationBarEvents, INavbarEventHandlersMap, INavbarConfig } from "./types";


export abstract class Navbar<T extends IItem = IItem> extends View {
	public data: TreeCollection<T>;
	public events: IEventSystem<DataEvents | NavigationBarEvents, IDataEventsHandlersMap & INavbarEventHandlersMap>;

	public config: INavbarConfig;

	protected _vpopups: VNode;
	protected _activeMenu: string;
	protected _activePosition: {
		left: number,
		right: number,
		top: number,
		bottom: number
	};
	protected _isContextMenu: boolean = false;

	protected _handlers: IHandlers;
	protected _currentRoot: string;

	protected _factory: (item: T, asMenuItem?: boolean) => any;

	protected _groups: IGroups;

	private _isActive: boolean;
	private _popupActive: boolean;
	private _currentTimeout: any;
	private _documentClick: (e: MouseEvent) => void;
	private _documentHaveListener: boolean = false;

	private _rootItem: any;
	private _activeParents: string[];

	constructor(element?: string | HTMLElement, config?: any) {
		super(element, extend({}, config));
		this._rootItem = {};

		if (Array.isArray(this.config.data)) {
			this.events = new EventSystem<DataEvents | NavigationBarEvents>(this);
			this.data = new TreeCollection<T>({}, this.events);
		} else if (this.config.data && this.config.data.events) {
			this.data = this.config.data;
			this.events = this.data.events;
			this.events.context = this;
		} else {
			this.events = new EventSystem<DataEvents | NavigationBarEvents>(this);
			this.data = new TreeCollection<T>({}, this.events);
		}

		this._documentClick = (e) => {
			if (locate(e, "dhx_widget_id") !== this._uid && this._documentHaveListener) {
				document.removeEventListener("click", this._documentClick);
				this._documentHaveListener = false;
				this._close(e);
			}
		};
		this._currentRoot = this.data.getRoot();
		this._factory = this._getFactory();
		this._initHandlers();
		this._init();
		this._initEvents();
		if (Array.isArray(this.config.data)) {
			this.data.parse(this.config.data);
		}
	}
	paint(): void {
		super.paint();
		this._vpopups.redraw();
	}
	disable(ids: string | string[]) {
		this._setProp(ids, "disabled", true);
	}
	enable(ids: string | string[]) {
		this._setProp(ids, "disabled", false);
	}
	show(ids: string | string[]): void {
		this._setProp(ids, "hidden", false);
	}
	hide(ids: string | string[]): void {
		this._setProp(ids, "hidden", true);
	}
	destructor() {
		this.unmount();
		keyManager.removeHotKey(null, this);
		this._vpopups.unmount();
	}
	protected abstract _getFactory(): (item: T, asMenuItem?: boolean) => any;
	protected _customHandlers() {
		return {};
	}
	protected _close(e: MouseEvent): void {
		if (!this._popupActive || !this.events.fire(NavigationBarEvents.beforeHide, [this._activeMenu, e])) {
			return;
		}
		if (this._activeParents) {
			this._activeParents.forEach(parentId => this.data.exists(parentId) && this.data.update(parentId, { $activeParent: false }));
		}
		if (this.config.navigationType === NavigationType.click) {
			this._isActive = false;
		}
		clearTimeout(this._currentTimeout);
		this._popupActive = false;
		this._activeMenu = null;
		this.events.fire(NavigationBarEvents.afterHide, [e]);
		this.paint();
	}
	protected _init() {
		const render = () => el(
			"div",
			{
				dhx_widget_id: this._uid,
				class: "dhx_" + (this._isContextMenu ? " dhx_context-menu" : ""),
				onmousemove: this._handlers.onmousemove,
				onmouseleave: this._handlers.onmouseleave,
				onclick: this._handlers.onclick,
				onmousedown: this._handlers.onmousedown
			},
			this._drawPopups()
		);
		this._vpopups = create({
			render
		});
		this._vpopups.mount(document.body);
	}
	protected _initHandlers() {
		/*
			for navigation type click:
			first click open menu, _isActive = true
			after navigation use mousemove
			can be closed after outer click or menu leaf item click
		*/
		this._isActive = this.config.navigationType !== NavigationType.click;

		this._handlers = {
			onmousemove: (e: MouseEvent) => {
				if (!this._isActive) {
					return;
				}

				const elem = locateNode(e);
				if (!elem) {
					return;
				}
				const id = elem.getAttribute("dhx_id");
				if (this._activeMenu !== id) {
					if (this.data.haveItems(id)) {
						const position = getRealPosition(elem as HTMLElement);
						this.data.update(id, { $position: position }, false);
					}
					this._activeItemChange(id, e);
				}
			},
			onmouseleave: (e: MouseEvent) => {
				if (this.config.navigationType !== NavigationType.click) { // maybe all time when mouse leave close menu
					if (this._popupActive) {
						const element = locateNode(e, "dhx_id", "relatedTarget");
						if (element) {
							const id = element.getAttribute("dhx_id");
							if (!this.data.getItem(id)) {
								this._close(e);
							}
						} else {
							this._close(e);
						}
					}
					this._activeItemChange(null, e);
				}
			},
			onclick: (e: MouseEvent) => {
				const element = locateNode(e);
				if (!element) {
					return;
				}

				const id = element.getAttribute("dhx_id");
				const item = this.data.getItem(id);
				if (item.multiClick) {
					return;
				}

				if (this.data.haveItems(id)) {
					if (id === this._currentRoot) {
						return;
					}
					if (!this._isActive) {
						this._isActive = true;
					}
					this._setRoot(id);
					const position = getRealPosition(element as HTMLElement);
					this.data.update(id, { $position: position }, false);
					this._activeItemChange(id, e);
				} else {
					switch (item.type) {
						case ItemType.input:
						case ItemType.title:
							break;
						case ItemType.menuItem:
						case ItemType.selectButton:
							this._onMenuItemClick(id, e);
							break;
						case ItemType.imageButton:
						case ItemType.button:
						case ItemType.customHTMLButton:
						case ItemType.navItem:
							if ((item as any).twoState) {
								this.data.update(item.id, { active: !item.active });
							}
							this.events.fire(NavigationBarEvents.click, [id, e]);
							// missed break for trigger close
						default:
							this._close(e);
					}
				}
			},
			onmousedown: e => {
				const element = locateNode(e);
				if (!element) {
					return;
				}

				const id = element.getAttribute("dhx_id");
				const item = this.data.getItem(id);
				if (!item.multiClick) {
					return;
				}

				let fireTime = 365;
				let timeout;

				const fireAction = () => {
					this.events.fire(NavigationBarEvents.click, [id, e]);
					if (fireTime > 50) {
						fireTime -= 55;
					}

					timeout = setTimeout(fireAction, fireTime);
				};

				const mouseup = () => {
					clearTimeout(timeout);
					document.removeEventListener("mouseup", mouseup);
				};

				fireAction();

				document.addEventListener("mouseup", mouseup);
			},
			...this._customHandlers()
		};
	}
	protected _initEvents(): void {
		let timeout = null;
		this.data.events.on(DataEvents.change, () => {
			this.paint();

			if (timeout) {
				clearTimeout(timeout);
			}

			timeout = setTimeout(() => {
				const groups: IGroups = {};

				this.data.eachChild(this.data.getRoot(), item => {
					if (item.group) {
						(item as any).twoState = true;
						addInGroups(groups, item);
					}
				}, true);

				this._groups = groups;
				this._resetHotkeys();
				timeout = null;
				this.paint();
			}, 100);
		});

		this.events.on(NavigationBarEvents.click, id => {
			const item = this.data.getItem(id);
			const parent = this.data.getItem(item.parent);

			if (parent && parent.type === ItemType.selectButton) {
				this.data.update(item.parent, { value: item.value, icon: item.icon });
			}

			if (item.group) {
				const group = this._groups[item.group];

				if (group.active) {
					this.data.update(group.active, { active: false });
				}
				group.active = item.id;
				this.data.update(item.id, { active: true });
			}
		});
		this._customInitEvents();
	}
	protected _getMode(item: T, root: string, _active = false): "bottom" | "right" {
		return item.parent === root ? "bottom" : "right";
	}
	protected _drawMenuItems(id: string, asMenuItem: boolean = true) {
		return this.data.map(item => this._factory(item, asMenuItem), id, false);
	}
	protected _setRoot(_id: string): void {
		return; // need only for toolbar
	}
	protected _getParents(id, root): string[] {
		const parentIds = [];
		let afterRoot = false;

		const currentItem = this.data.getItem(id);
		const disabled = currentItem && currentItem.disabled;
		this.data.eachParent(id, item => {
			if (item.id === root) {
				parentIds.push(item.id);
				afterRoot = true;
			} else if (!afterRoot) {
				parentIds.push(item.id);
			}
		}, !disabled);

		if (this._isContextMenu && this._activePosition) {
			parentIds.push(root);
		}
		return parentIds;
	}
	protected _listenOuterClick() {
		if (this._documentHaveListener) {
			return;
		}
		document.addEventListener("click", this._documentClick, true);
		this._documentHaveListener = true;
	}
	protected _customInitEvents() {
		return;
	}
	private _drawPopups() {
		const id = this._activeMenu;
		if (!this._isContextMenu && !id) {
			return null;
		}
		const root = this._currentRoot;
		if (this._isContextMenu && !this._activePosition) {
			return null;
		}
		const parentIds = this._getParents(id, root);
		this._activeParents = parentIds;
		parentIds.forEach((parentId) => this.data.exists(parentId) && this.data.update(parentId, { $activeParent: true }, false));
		return parentIds.map(itemId => {
			if (!this.data.haveItems(itemId)) {
				return null;
			}

			const item = this.data.getItem(itemId) || this._rootItem as any; // for root item
			this._popupActive = true;
			return el("ul", {
				class: "dhx_widget dhx_menu" + (this.config.menuCss ? " " + this.config.menuCss : ""),
				_key: itemId,
				_hooks: {
					didInsert: vnode => {
						const { width, height } = vnode.el.getBoundingClientRect();
						const position = this._isContextMenu && this._activePosition && itemId === root ? this._activePosition : item.$position;
						const mode = this._getMode(item, root, position === this._activePosition);
						const style = calculatePosition(position, { mode: mode as any, width, height });
						item.$style = style;
						vnode.patch({ style });
					},
					didRecycle: (_, vnode) => {
						if (this._isContextMenu && this._activePosition && itemId === root) {
							const { width, height } = vnode.el.getBoundingClientRect();
							const style = calculatePosition(this._activePosition, { mode: this._getMode(item, root, true) as any, width, height });
							item.$style = style;
							vnode.patch({ style });
						}
					}
				},
				tabindex: 0,
				style: item.$style || {
					position: "absolute"
				}
			}, this._drawMenuItems(itemId));
		}).reverse();
	}

	private _onMenuItemClick(id, e) {
		const item = this.data.getItem(id);
		if (item.disabled) {
			return;
		}
		if ((item as any).twoState) {
			this.data.update(item.id, { active: !item.active });
		}
		this.events.fire(NavigationBarEvents.click, [id, e]);
		this._close(e);
	}
	private _activeItemChange(id: string, e: MouseEvent): void {
		if (this._activeParents) {
			const parentIds = this._getParents(id, this._currentRoot);
			this._activeParents.forEach(parentId => {
				if (this.data.exists(parentId) && parentIds.indexOf(parentId) === -1) {
					this.data.update(parentId, { $activeParent: false }, false);
				}
			});
		}
		if (id && !this._documentHaveListener) {
			this._listenOuterClick();
		}
		if (id && this.data.haveItems(id)) {
			if (this._activeMenu !== id || !this._popupActive) {
				this.events.fire(NavigationBarEvents.openMenu, [id]);
			}
			this._activeMenu = id;
			clearTimeout(this._currentTimeout);
			this.paint();
		} else {
			clearTimeout(this._currentTimeout);
			this._currentTimeout = setTimeout(() => this.paint(), 400);
		}
	}
	private _resetHotkeys(): void {
		keyManager.removeHotKey(null, this);
		this.data.map(item => {
			if (item.hotkey) {
				keyManager.addHotKey(
					item.hotkey,
					() => this._onMenuItemClick(item.id, null),
					this
				);
			}
		});
	}
	private _setProp(id: string | string[], key: string, value: any) {
		if (Array.isArray(id)) {
			id.forEach(itemId => this.data.update(itemId, { [key]: value }));
		} else {
			this.data.update(id, { [key]: value });
		}
	}
}

function addInGroups(groups: IGroups, item: any) {
	if (groups[item.group]) {
		if (item.active) {
			groups[item.group].active = item.id;
		}
		groups[item.group].elements.push(item.id);
	} else {
		groups[item.group] = {
			active: item.active ? item.id : null,
			elements: [item.id]
		};
	}
}